import scrollIntoView from 'scroll-into-view'
import { Checkbox, Popover, Typography } from 'antd'
import {
  ArticleColumn,
  ArticleDefaultColumn,
  ArticleDefaultMarketplaceColLength,
  KpiColWidth,
  ListingColWidth,
  SizeSpecialColumn,
  SizeWithAvailabilitySpecialColumn,
  subRuleListingDiscount,
  subRuleListingMargin,
  subRuleListingPurchased,
} from '../Constants/TableConfiguration'
import { TranslationKeys } from '../Constants/TranslationKeys'
import { getCurrencyConversion, getCurrencySymbolByPricelist, renderTranslation } from './GenericHelpers'
import AdditionalMenu from '../Components/Table/TableComponents/AdditionalMenu'
import TitleDynamicLabel from '../Components/Table/TableComponents/TitleDynamicLabel'
import SubRuleAdditionalMenu from '../Components/Omnichannel/SubRuleAdditionalMenu'
import SubRuleDynamicLabel from '../Components/Omnichannel/SubRuleDynamicLabel'
import BarcodeRecapPopover from '../Components/Table/TableComponents/BarcodeRecapPopover'
import BarcodeEditPopover from '../Components/Table/TableComponents/BarcodeEditPopover'
import DynamicParagraph from '../Components/Table/TableComponents/DynamicParagraph'
import LocalizedSpan from '../Components/Table/TableComponents/LocalizedSpan'
import { calculateColumnFreeWidth, retrieveColumnWidth } from './GenericHelpers'
import { getImageFromUrl } from '../Services/PIM/ImageService'
import { showImageModal } from '../Slices/Kiosk/ArticleSlice'
import SubRuleListingInput from '../Components/Omnichannel/SubRuleListingInput'
import {
  TableColumnTranslationPrefix,
  brandParam,
  missingImageGen,
  notFoundImageGen,
  sectorParam,
  supergroupParam,
  supergroupSuperParam,
} from '../Constants/Configuration'
import { setCapitalLetter } from './StringHelpers'
import PriceListSizePopover from '../Components/Table/TableComponents/PriceListSizePopover'
import { saveSorting } from '../Slices/Kiosk/TableOptionsSlice'
import { populateSubRuleTableWithArticles } from '../Services/Omnichannel/SeasonRuleService'
import { populateTableWithArticles } from '../Services/Kiosk/ArticleServices'
import { saveSorting as saveSubruleSorting } from '../Slices/Omnichannel/SubRuleSlice'
import { ITag } from 'Services/Kiosk/TagsService'

const { Text } = Typography

const scrollToTableRow = recordKey => {
  const element = document.querySelector('[data-row-key="' + recordKey + '"]')

  scrollIntoView(element, {
    align: {
      top: 0.1,
      lockX: true,
    },
    validTarget: function (target, parentsScrolled) {
      return parentsScrolled === 1 && target !== window && target.classList.contains('ant-table-body')
    },
    isScrollable: (target, defaultIsScrollable) => {
      return defaultIsScrollable(target) && target !== window && target.classList.contains('ant-table-body')
    },
  })
}

const scrollElementHeightPositionHandler = (mouseDownEvent, ySize, updateTableSize) => {
  mouseDownEvent.preventDefault()

  const startSize = ySize
  const startPosition = { y: mouseDownEvent.pageY }

  function onMouseMove(mouseMoveEvent) {
    const size = startSize - startPosition.y + mouseMoveEvent.pageY
    updateTableSize(size)
  }
  function onMouseUp() {
    document.body.removeEventListener('mousemove', onMouseMove)
    // uncomment the following line if not using `{ once: true }`
    // document.body.removeEventListener("mouseup", onMouseUp);
  }

  document.body.addEventListener('mousemove', onMouseMove)
  document.body.addEventListener('mouseup', onMouseUp, { once: true })
}

const scrollElementHeightPositionTouchHandler = (touchEvent, ySize, updateTableSize) => {
  const touchTarget = touchEvent.targetTouches[0]

  const startSize = ySize
  const startPosition = { y: touchTarget.pageY }

  function onTouchMove(touchEvent) {
    const touchTargetMove = touchEvent.targetTouches[0]
    const size = startSize - startPosition.y + touchTargetMove.pageY
    updateTableSize(size)
  }
  function onTouchUp() {
    document.body.removeEventListener('touchmove', onTouchMove)
    // uncomment the following line if not using `{ once: true }`
    // document.body.removeEventListener("mouseup", onMouseUp);
  }

  document.body.addEventListener('touchmove', onTouchMove)
  document.body.addEventListener('touchend', onTouchUp, { once: true })
}

const generateSpecialColumn = (language, filterByAvailability, toggleAvailability, sizeFilter) => {
  return {
    title: (
      <div className='availability-checkbox-container'>
        <div className='availability-checkbox-standalone'>
          <Checkbox
            checked={filterByAvailability}
            onChange={toggleAvailability}
            className='availability-checkbox general-checkbox-alt'
          ></Checkbox>
        </div>
        <div className='custom-article-title'>
          {renderTranslation(language, TranslationKeys.acronymTableAvailability)}
        </div>
      </div>
    ),
    dataIndex: ArticleColumn.Availability,
    key: ArticleColumn.Availability,
    align: 'center',
    width: SizeSpecialColumn,
    className: 'availability-column',
    render: text => convertAvailabilityToYesNoBySizeFilter(text, sizeFilter, language),
  }
}

const generateSpecialColumnWithSize = (language, filterByAvailability, toggleAvailability, sizeFilter) => {
  return {
    title: (
      <AdditionalMenu disableDots label={ArticleColumn.Size}>
        <TitleDynamicLabel label={ArticleColumn.Size} />
        <div className='availability-checkbox-container'>
          <div className='custom-article-title'>
            <Checkbox
              checked={filterByAvailability}
              onChange={toggleAvailability}
              className='availability-checkbox general-checkbox-alt'
            ></Checkbox>
            {renderTranslation(language, TranslationKeys.acronymTableAvailability)}
          </div>
        </div>
      </AdditionalMenu>
    ),
    dataIndex: ArticleColumn.Availability,
    key: ArticleColumn.Availability,
    align: 'center',
    width: SizeWithAvailabilitySpecialColumn,
    className: 'size-availability-column',
    render: text => {
      return (
        <div className='d-flex-c'>
          <div className='adjust-size-cell-element' />
          <div className='size-number-container'>
            {convertAvailabilityToYesNoBySizeFilter(text, sizeFilter, language)}
          </div>
          <div />
        </div>
      )
    },
  }
}

const convertAvailabilityToYesNoBySizeFilter = (text, sizeFilter, language) => {
  if (sizeFilter) return text
  if (text > 0) return renderTranslation(language, TranslationKeys.tableDotsMenuMktYes)
  else return renderTranslation(language, TranslationKeys.tableDotsMenuMktNo)
}

const generateTableColumnsToRender = (
  columns,
  articles,
  language,
  defaultCols,
  freeWidthColumns,
  allFreeWidthColumns,
  editTableDisable,
  pricelists,
  priceList1,
  priceList2,
  isPurchasePriceVisibile,
  alignLeftColumns,
  alignRightColumns,
  newLineColumns,
  negateTableColumns,
  filterByAvailability,
  toggleAvailability,
  isConfigurationFormGruccia,
  isFromDiWa,
  dispatch,
  sizeFilter,
  isCarryOver?,
) => {
  const renderedColumns = []

  if (columns.length > 0) {
    columns.forEach(x => {
      if (x.key === ArticleColumn.Availability) {
        if (!isConfigurationFormGruccia) {
          if (columns.find(x => x.name === ArticleColumn.Size)) {
            renderedColumns.push(
              generateSpecialColumnWithSize(language, filterByAvailability, toggleAvailability, sizeFilter),
            )
          } else {
            if (isFromDiWa) {
              renderedColumns.push(
                generateSpecialColumn(language, filterByAvailability, toggleAvailability, sizeFilter),
              )
            }
          }
        }
      } else if (x.name === ArticleColumn.Size) {
        //nothing
      } else if (x.name === ArticleColumn.ArticleCode) {
        const width =
          allFreeWidthColumns || freeWidthColumns.indexOf(x.name) !== -1
            ? calculateColumnFreeWidth(
                x.name,
                x.price,
                x.language,
                x.isMarketplace,
                language,
                defaultCols,
                articles,
                dispatch,
              )
            : retrieveColumnWidth(x.name)
        renderedColumns.push({
          title: isFromDiWa ? (
            <AdditionalMenu fixed={'left'} label={x.name}>
              <TitleDynamicLabel label={x.name} style={{ maxWidth: '92%' }} />
            </AdditionalMenu>
          ) : (
            <SubRuleAdditionalMenu fixed={'left'} label={x.name}>
              <SubRuleDynamicLabel label={x.name} style={{ maxWidth: '92%' }} />
            </SubRuleAdditionalMenu>
          ),
          dataIndex: x.name,
          key: x.name,
          fixed: 'left',
          align: x.align,
          render: (text, record, index) =>
            record.images.length !== 0 ? (
              <div className='d-flex-c'>
                <img
                  src={getImageFromUrl(record['image'])}
                  className='cursor-pointer pr-2'
                  onClick={e => {
                    e.stopPropagation()
                    dispatch(showImageModal(record.key))
                  }}
                  alt=''
                  width={30}
                  height={30}
                  onError={e => {
                    const target = e.target as HTMLImageElement
                    target.onerror = null
                    target.src = notFoundImageGen
                  }}
                />
                {text && text.length >= 30 ? (
                  <Popover content={text} trigger='hover'>
                    <span className='article-code-label truncate-text'>{text.substr(0, 30) + '...'}</span>
                  </Popover>
                ) : (
                  <span className='article-code-label truncate-text'>{text}</span>
                )}
              </div>
            ) : (
              <div className='d-flex-c'>
                <img src={missingImageGen} className='cursor-pointer pr-2' alt='' width={30} height={30} />
                {text && text.length >= 30 ? (
                  <Popover content={text} trigger='hover'>
                    <span className='article-code-label truncate-text'>{text.substr(0, 30) + '...'}</span>
                  </Popover>
                ) : (
                  <span className='article-code-label truncate-text'>{text}</span>
                )}
              </div>
            ),
          width: width,
          className: negateTableColumns.indexOf(x.name) !== -1 ? 'empty' : '',
        })
      } else if (x.price) {
        const fixed =
          alignLeftColumns.indexOf(x.key) !== -1
            ? 'left'
            : alignRightColumns.indexOf(x.key) !== -1
            ? 'right'
            : undefined

        const priceListName =
          x.pricelist === 1 ? pricelists.priceList1 : x.pricelist === 2 ? pricelists.priceList2 : undefined
        const activePriceList = x.pricelist === 1 ? priceList1 : priceList2

        const currencySymbol = getCurrencySymbolByPricelist(activePriceList, priceListName)

        if (
          (isPurchasePriceVisibile && x.name === ArticleColumn.GrossWholesalePrice) ||
          (isPurchasePriceVisibile && x.name === ArticleColumn.WholesalePrice) ||
          (x.name !== ArticleColumn.GrossWholesalePrice && x.name !== ArticleColumn.WholesalePrice)
        ) {
          renderedColumns.push({
            title: (
              <AdditionalMenu fixed={fixed} label={x.key}>
                <div className='custom-article-title pr-2'>
                  <LocalizedSpan languageKey={x.name} pricelist={x.pricelist} />
                </div>
              </AdditionalMenu>
            ),
            dataIndex: x.key,
            key: x.key,
            fixed: fixed,
            align: 'right',
            render: (text, record) =>
              Object.keys(record?.priceListResult ?? {}).length > 0 ? (
                (record?.priceListResult[priceListName] ?? []).length > 1 ? (
                  <PriceListSizePopover
                    priceAndSizes={record?.priceListResult[priceListName]}
                    currencySymbol={currencySymbol}
                    field={x.name}
                  >
                    <Text className='cursor-pointer text-underline'>
                      {renderTranslation(language, TranslationKeys.scattoTaglia)}
                    </Text>
                  </PriceListSizePopover>
                ) : (
                  <div>
                    {getCurrencyConversion(
                      record?.priceListResult[priceListName] ? record.priceListResult[priceListName][0][x.name] : 0,
                    )}
                    {' ' + currencySymbol}
                  </div>
                )
              ) : (
                <div>
                  {getCurrencyConversion(text ?? 0)}
                  {' ' + currencySymbol}
                </div>
              ),
            width:
              allFreeWidthColumns || freeWidthColumns.indexOf(x.name) !== -1
                ? calculateColumnFreeWidth(
                    x.name,
                    x.price,
                    x.language,
                    x.isMarketplace,
                    language,
                    defaultCols,
                    articles,
                    dispatch,
                  )
                : retrieveColumnWidth(x.name),
          })
        }
      } else if (x.name === ArticleColumn.ProductDescription) {
        const fixed =
          alignLeftColumns.indexOf(x.name) !== -1
            ? 'left'
            : alignRightColumns.indexOf(x.name) !== -1
            ? 'right'
            : undefined
        const width =
          allFreeWidthColumns || freeWidthColumns.indexOf(x.name) !== -1
            ? calculateColumnFreeWidth(
                x.name,
                x.price,
                x.language,
                x.isMarketplace,
                language,
                defaultCols,
                articles,
                dispatch,
              )
            : retrieveColumnWidth(x.name)
        renderedColumns.push({
          title: isFromDiWa ? (
            <AdditionalMenu fixed={fixed} label={x.name}>
              <TitleDynamicLabel label={x.name} className='max-w-90' />
            </AdditionalMenu>
          ) : (
            <SubRuleAdditionalMenu fixed={'left'} label={x.name} width={width}>
              <SubRuleDynamicLabel label={x.name} className='max-w-90' />
            </SubRuleAdditionalMenu>
          ),
          dataIndex: x.name,
          key: x.name,
          fixed: fixed,
          align: x.align,
          render: (text, record, index) => (
            <DynamicParagraph elementWidth={width} text={text} language={x.language} colName={x.name} />
          ),
          width: width,
          className: negateTableColumns.indexOf(x.name) !== -1 ? 'empty' : '',
        })
      } else if (x.language) {
        const fixed =
          alignLeftColumns.indexOf(x.key) !== -1
            ? 'left'
            : alignRightColumns.indexOf(x.key) !== -1
            ? 'right'
            : undefined
        const width =
          allFreeWidthColumns || freeWidthColumns.indexOf(x.name) !== -1
            ? calculateColumnFreeWidth(
                x.name,
                x.price,
                x.language,
                x.isMarketplace,
                language,
                defaultCols,
                articles,
                dispatch,
              )
            : retrieveColumnWidth(x.name)
        renderedColumns.push({
          title: (
            <AdditionalMenu fixed={fixed} label={x.key} name={x.name} language={x.language}>
              <TitleDynamicLabel disableSearch label={x.name} language={x.language} style={{ maxWidth: '92%' }} />
            </AdditionalMenu>
          ),
          dataIndex: x.key,
          key: x.key,
          fixed: fixed,
          align: x.align,
          render: (text, record, index) => (
            <DynamicParagraph elementWidth={width} text={text} language={x.language} colName={x.name} />
          ),
          width: width,
          className: negateTableColumns.indexOf(x.name) !== -1 ? 'empty' : '',
        })
      } else if (x.isMarketplace) {
        const fixed =
          alignLeftColumns.indexOf(x.mktColumn) !== -1
            ? 'left'
            : alignRightColumns.indexOf(x.mktColumn) !== -1
            ? 'right'
            : undefined
        renderedColumns.push({
          title: (
            <AdditionalMenu fixed={fixed} label={x.name} isMarketplace={x.isMarketplace} mktColumn={x.mktColumn}>
              <TitleDynamicLabel label={x.name} isMarketplace={x.isMarketplace} mktColumn={x.mktColumn} />
            </AdditionalMenu>
          ),
          dataIndex: x.mktColumn,
          key: x.mktColumn,
          fixed: fixed,
          align: x.align,
          render: (text, record, index) =>
            x.mktColumn && record[x.mktColumn.toLowerCase()] ? (
              <div className='text-center'>{renderTranslation(language, TranslationKeys.tableMktYes)}</div>
            ) : (
              <div className='text-center'>{renderTranslation(language, TranslationKeys.tableMktNo)}</div>
            ),
          width:
            allFreeWidthColumns || freeWidthColumns.indexOf(x.name) !== -1
              ? calculateColumnFreeWidth(
                  x.name,
                  x.price,
                  x.language,
                  x.isMarketplace,
                  language,
                  defaultCols,
                  articles,
                  dispatch,
                )
              : ArticleDefaultMarketplaceColLength,
          className: negateTableColumns.indexOf(x.name) !== -1 ? 'empty' : '',
        })
      } else if (x.isListing) {
        renderedColumns.push({
          title: x.name,
          dataIndex: x.name,
          key: x.name,
          width: ListingColWidth,
          className: 'listing-title-locked article-title',
          children: [
            {
              title: renderTranslation(language, TranslationKeys.tableSubRuleListingPurchased),
              dataIndex: subRuleListingPurchased + x.name,
              key: subRuleListingPurchased + x.name,
              width: ListingColWidth / 3,
              className: 'listing-col-locked article-title',
              render: (text, record, index) => (
                <Text className='text-article' ellipsis={true} style={{ width: ListingColWidth / 3 }}>
                  {getCurrencyConversion(text)}
                </Text>
              ),
            },
            {
              title: x.isTypeSell ? renderTranslation(language, TranslationKeys.tableSubRuleListingDiscount) : 'RI %',
              dataIndex: subRuleListingDiscount + x.name,
              key: subRuleListingDiscount + x.name,
              width: ListingColWidth / 3,
              className: 'listing-col-locked article-title',
              render: (text, record, index) => (
                <SubRuleListingInput editTableDisable={editTableDisable} recordInfo={record} channel={x.channelKey ?? x.name} />
              ),
            },
            {
              title: renderTranslation(language, TranslationKeys.tableSubRuleListingMargin),
              dataIndex: subRuleListingMargin + x.name,
              key: subRuleListingMargin + x.name,
              width: ListingColWidth / 3,
              className: 'listing-col-locked article-title',
              render: (text, record, index) => (
                <Text className='text-article' ellipsis={true} style={{ width: ListingColWidth / 3 }}>
                  {getCurrencyConversion(text)}
                </Text>
              ),
            },
          ],
        })
      } else if (x.subRuleLockedColumn) {
        renderedColumns.push({
          title: renderTranslation(
            language,
            TranslationKeys[TableColumnTranslationPrefix + 'SubRule' + setCapitalLetter(x.name)],
          ),
          dataIndex: x.name,
          key: x.name,
          render: (text, record, index) => (
            <Text className='text-article' ellipsis={true} style={{ width: KpiColWidth }}>
              {getCurrencyConversion(text)}
            </Text>
          ),
          width: KpiColWidth,
          className: 'subrule-col-locked article-title',
        })
      } else {
        if (
          isFromDiWa ||
          (x.name !== ArticleColumn.Season && !isCarryOver) ||
          (x.name === ArticleColumn.Season && isCarryOver)
        ) {
          const fixed =
            alignLeftColumns.indexOf(x.name) !== -1
              ? 'left'
              : alignRightColumns.indexOf(x.name) !== -1
              ? 'right'
              : undefined
          const width =
            allFreeWidthColumns || freeWidthColumns.indexOf(x.name) !== -1
              ? calculateColumnFreeWidth(
                  x.name,
                  x.price,
                  x.language,
                  x.isMarketplace,
                  language,
                  defaultCols,
                  articles,
                  dispatch,
                )
              : retrieveColumnWidth(x.name)
          const isNewLineActive = newLineColumns.indexOf(x.name) !== -1
          renderedColumns.push({
            title:
              x.name === ArticleColumn.ProductCode ? (
                isFromDiWa ? (
                  <AdditionalMenu disableDots fixed={fixed} label={x.name}>
                    <TitleDynamicLabel label={x.name} style={{ maxWidth: '92%' }} />
                  </AdditionalMenu>
                ) : (
                  <SubRuleAdditionalMenu disableDots fixed={fixed} label={x.name}>
                    <SubRuleDynamicLabel label={x.name} style={{ maxWidth: '92%' }} />
                  </SubRuleAdditionalMenu>
                )
              ) : x.name === ArticleColumn.Barcode ? (
                <AdditionalMenu fixed={fixed} label={x.name}>
                  <TitleDynamicLabel disableSearch label={x.name} style={{ maxWidth: '93%' }} />
                </AdditionalMenu>
              ) : isFromDiWa ? (
                <AdditionalMenu fixed={fixed} label={x.name}>
                  <TitleDynamicLabel
                    label={x.name}
                    style={{ maxWidth: '93%' }}
                    // multipleSearch={x.multipleSearch}
                  />
                </AdditionalMenu>
              ) : (
                <SubRuleAdditionalMenu fixed={fixed} label={x.name}>
                  <SubRuleDynamicLabel label={x.name} style={{ maxWidth: '92%' }} />
                </SubRuleAdditionalMenu>
              ),
            dataIndex: x.name,
            key: x.name,
            fixed: fixed,
            align: x.align,
            render: (text, record, index) => {
              if(x.name === ArticleColumn.Barcode) {
                return record.publicBarcode ? (
                  editTableDisable ? (
                    <BarcodeRecapPopover publicBarcode={record.publicBarcode}>
                      <Text className='cursor-pointer'>{text}</Text>
                    </BarcodeRecapPopover>
                  ) : (
                    <BarcodeEditPopover publicBarcode={record.publicBarcode} vaId={record.key}>
                      <Text className='cursor-pointer'>{text}</Text>
                    </BarcodeEditPopover>
                  )
                ) : (
                  <Text className='cursor-pointer'>{text}</Text>
                )
              } else if(x.name === ArticleColumn.Tag) {
                const text = record?.tags?.map((x: ITag) => {
                  return x?.slug
                }).join(", ");
                return record.tags && record.tags.length > 0 ? 
                <DynamicParagraph elementWidth={width} text={text} language={x.language} colName={x.name} /> : null
              } else {
                return isNewLineActive ? (
                  <DynamicParagraph elementWidth={width} text={text} language={x.language} colName={x.name} />
                ) : (
                  <Text ellipsis={true} style={{ width: width }}>
                    {text}
                  </Text>
                )
              }
            },
            width: width,
            className: negateTableColumns.indexOf(x.name) !== -1 ? 'empty' : '',
          })
        }
      }
    })
  }

  return renderedColumns
}

const mapArticleImagesAndDescription = articles => {
  return articles.map(element => {
    const result = { ...element }
    if (element.images.length) {
      const defaults = element.images.filter(x => x.default)
      if (defaults.length) {
        result['image'] = defaults[0].url
      } else {
        result['image'] = element.images[0].url
      }
    } else {
      result['image'] = ''
    }
    result[ArticleColumn.Description].forEach(langDescription => {
      result[ArticleColumn.Description + langDescription.languageCode] = langDescription.value
    })
    result[ArticleColumn.ShortDescription].forEach(langDescription => {
      result[ArticleColumn.ShortDescription + langDescription.languageCode] = langDescription.value
    })
    result[ArticleColumn.SpecialDescription].forEach(langDescription => {
      result[ArticleColumn.SpecialDescription + langDescription.languageCode] = langDescription.value
    })
    result[ArticleColumn.SizeAndFit].forEach(langDescription => {
      result[ArticleColumn.SizeAndFit + langDescription.languageCode] = langDescription.value
    })
    result['marketplaceAvailability'].forEach(avail => {
      result[avail.name.toLowerCase()] = avail.available
    })

    if (result.channels.length > 0) {
      result.channels.forEach(channel => {
        result[subRuleListingPurchased + channel.channel] = channel[subRuleListingPurchased]
        result[subRuleListingDiscount + channel.channel] = channel[subRuleListingDiscount]
        result[subRuleListingMargin + channel.channel] = channel[subRuleListingMargin]
      })
    }

    return result
  })
}

const retrieveColumnKey = (param: string): string => {
  switch (param) {
    case supergroupParam:
      return ArticleColumn.Category
    case supergroupSuperParam:
      return ArticleColumn.SuperCategory
    case sectorParam:
      return ArticleColumn.Department
    case brandParam:
      return ArticleColumn.Brand
    default:
      return ''
  }
}

const triggerPagination = (sorter, pagination, isFromDiwa: boolean, editDisabled?: boolean) => (dispatch, getState) => {
  if (isFromDiwa) {
    dispatch(
      saveSorting({
        ...sorter,
        ...pagination,
      }),
    )
    dispatch(populateTableWithArticles(true))
  } else {
    dispatch(saveSubruleSorting({ ...sorter, ...pagination }))
    dispatch(populateSubRuleTableWithArticles(true))
  }
}

const calcRuleTableHeight = elementHeight => {
  const brandTableHeaderHeight: HTMLElement = document.querySelector(
    '#discount-main-table > div > div.ant-table-header',
  )

  //10 is for spacing and scrollbars
  return elementHeight - 20 - (brandTableHeaderHeight?.offsetHeight ?? 167.5)
}

const supergroupsTablesInitialized = supergroups => {
  const scrollableTables = document.querySelectorAll('.same-table-scroll .virtual-list')
  return scrollableTables.length > 0 && scrollableTables.length === supergroups
}

const mapSizeColumnData = data => {
  return data.map(element => {
    const result = { ...element }
    if (element.sizes && element.sizes.length) {
      element.sizes.forEach(size => {
        result['detail' + size.name] = size.quantity
      })
    }

    return result
  })
}

const handleAvailabiltyAndSizeColumnPosition = (
  visibleColumns: ArticleDefaultColumn[],
  rightFixedColumn?: ArticleDefaultColumn[],
) => {
  if (visibleColumns.length === 0) {
    return
  }

  //set available column
  const indexToSet = rightFixedColumn ? visibleColumns.length - rightFixedColumn.length : visibleColumns.length
  visibleColumns.splice(indexToSet, 0, { key: ArticleColumn.Availability })

  const sizeIndex = visibleColumns.findIndex(x => x.name === ArticleColumn.Size)

  //size is moved to last index to prevent colum delete instead of other colums, because it create conflict with the array order
  if (sizeIndex !== -1) {
    visibleColumns.push(visibleColumns.splice(sizeIndex, 1)[0])
  }
}

export {
  scrollToTableRow,
  scrollElementHeightPositionHandler,
  scrollElementHeightPositionTouchHandler,
  generateTableColumnsToRender,
  mapArticleImagesAndDescription,
  retrieveColumnKey,
  triggerPagination,
  calcRuleTableHeight,
  supergroupsTablesInitialized,
  mapSizeColumnData,
  handleAvailabiltyAndSizeColumnPosition,
}
